// Meeting lifecycle + Picture-in-Picture manager.
//
// One module owns the answer to a single question: "is the user still in the
// meeting?" — which is deliberately NOT the same question as "is the page
// visible?". Page hide / SPA route change / back navigation never mean the
// user left; only the explicit Leave Call action does.
//
// Browser reality this implementation is built around:
//   * video.requestPictureInPicture() requires *transient user activation* in
//     Chrome/Edge. It CANNOT be called from visibilitychange / pagehide /
//     popstate — those events carry no activation and the promise rejects with
//     NotAllowedError. The old implementation called it exactly there, which is
//     why PiP never opened.
//   * The only automatic (no-gesture) entry Chrome allows is the
//     "Auto Picture-in-Picture" capability, which is granted to *installed*
//     PWAs (display-mode: standalone) via the autoPictureInPicture attribute.
//   * Safari uses webkitSetPresentationMode('picture-in-picture').
//   * Document Picture-in-Picture (documentPictureInPicture.requestWindow) also
//     needs user activation, so it is used as a *fallback renderer*, never as a
//     way to bypass the activation rule.
//
// Strategy: keep a short activation window (a real gesture arms PiP for
// ACTIVATION_MS). If the page is hidden / navigated away inside that window we
// enter PiP immediately. Outside it we mark PiP as pending and enter on the very
// next gesture, so a background→foreground→background cycle still works.

const ACTIVATION_MS = 5000;

const state = {
  meetingActive: false,
  userExplicitlyLeft: false,
  pipActive: false,
  pageHidden: false,
  navigationAway: false,
  isRestoringMeeting: false,
  pipPending: false,
  keepFloating: false,
  lastGestureAt: 0,
  lastError: null,
};

let debug = false;
try {
  debug =
    typeof window !== "undefined" &&
    (window.localStorage?.getItem("udyogPipDebug") === "1" ||
      /(?:\?|&)pipdebug=1/.test(window.location.search));
} catch (_) {}

const log = (...args) => {
  if (debug) console.log("[udyog-pip]", ...args);
};

export const pipDebugEnabled = () => debug;
export const getMeetingState = () => ({ ...state });

// ---------------------------------------------------------------- support

const supportsVideoPip = () =>
  typeof document !== "undefined" && !!document.pictureInPictureEnabled;

const supportsSafariPip = (v) =>
  !!v && typeof v.webkitSetPresentationMode === "function" && v.webkitSupportsPresentationMode?.("picture-in-picture");

const supportsDocumentPip = () =>
  typeof window !== "undefined" && !!window.documentPictureInPicture?.requestWindow;

const isInstalledPwa = () => {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true
    );
  } catch (_) {
    return false;
  }
};

export const pipSupport = () => ({
  videoPip: supportsVideoPip(),
  documentPip: supportsDocumentPip(),
  autoPipEligible: supportsVideoPip() && isInstalledPwa(),
});

// ---------------------------------------------------------------- elements

// The meeting renders LiveKit <video> elements whose srcObject is a
// MediaStream. Pick the biggest one that actually has decoded frames
// (screen share wins over camera because it is larger).
function pickVideo() {
  if (typeof document === "undefined") return null;
  const all = Array.from(document.querySelectorAll("video"));
  const live = all.filter((v) => {
    if (v.disablePictureInPicture) return false;
    if (v.dataset.udyogPipClone === "1" || v.dataset.udyogPipFallback === "1") return false;
    const stream = v.srcObject;
    const hasLiveTrack =
      !stream || typeof stream.getVideoTracks !== "function"
        ? true
        : stream.getVideoTracks().some((t) => t.readyState === "live" && t.enabled);
    return hasLiveTrack && v.videoWidth > 0 && v.videoHeight > 0 && v.readyState >= 2;
  });
  live.sort((a, b) => b.videoWidth * b.videoHeight - a.videoWidth * a.videoHeight);
  log("pickVideo", {
    total: all.length,
    eligible: live.length,
    chosen: live[0] ? `${live[0].videoWidth}x${live[0].videoHeight}` : null,
    readyStates: all.map((v) => v.readyState),
  });
  return live[0] || null;
}

// Installed-PWA auto-PiP hint. Harmless elsewhere; only Chrome-as-PWA acts on it.
export function tagVideosForAutoPip() {
  if (typeof document === "undefined") return;
  const eligible = pipSupport().autoPipEligible;
  document.querySelectorAll("video").forEach((v) => {
    try {
      v.playsInline = true;
      v.disablePictureInPicture = false;
      if (eligible) v.setAttribute("autopictureinpicture", "");
      else v.removeAttribute("autopictureinpicture");
    } catch (_) {}
  });
}

// ---------------------------------------------------------------- fallback video

// With every camera off there is no <video> to pop out, so PiP is impossible.
// We therefore keep a tiny canvas stream (class branding + timer) that can
// always be handed to the browser's floating window.
let fallbackVideo = null;
let fallbackTimer = null;

function ensureFallbackVideo() {
  if (fallbackVideo?.srcObject) return fallbackVideo;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 640;
    canvas.height = 360;
    const ctx = canvas.getContext("2d");
    const startedAt = Date.now();
    const draw = () => {
      const secs = Math.floor((Date.now() - startedAt) / 1000);
      const mm = String(Math.floor(secs / 60)).padStart(2, "0");
      const ss = String(secs % 60).padStart(2, "0");
      ctx.fillStyle = "#070908";
      ctx.fillRect(0, 0, 640, 360);
      ctx.fillStyle = "rgba(16,185,129,0.16)";
      ctx.beginPath();
      ctx.arc(520, 60, 130, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#10b981";
      ctx.font = "600 34px Outfit, system-ui, sans-serif";
      ctx.fillText("UDYOG", 40, 150);
      ctx.fillStyle = "#ffffff";
      ctx.font = "500 22px Outfit, system-ui, sans-serif";
      ctx.fillText("Class in progress", 40, 195);
      ctx.fillStyle = "rgba(255,255,255,0.55)";
      ctx.font = "400 18px Outfit, system-ui, sans-serif";
      ctx.fillText(`Tap to return · ${mm}:${ss}`, 40, 232);
    };
    draw();
    fallbackTimer = setInterval(draw, 1000);
    const stream = canvas.captureStream(10);
    const v = document.createElement("video");
    v.dataset.udyogPipFallback = "1";
    v.muted = true;
    v.playsInline = true;
    v.autoplay = true;
    v.srcObject = stream;
    v.style.cssText = "position:fixed;left:-10000px;top:0;width:320px;height:180px;opacity:0.01;";
    document.body.appendChild(v);
    v.play().catch(() => {});
    fallbackVideo = v;
    log("fallback canvas video created");
    return v;
  } catch (e) {
    log("fallback video failed", e?.message);
    return null;
  }
}

function destroyFallbackVideo() {
  try {
    if (fallbackTimer) clearInterval(fallbackTimer);
    fallbackTimer = null;
    if (fallbackVideo) {
      fallbackVideo.srcObject?.getTracks?.().forEach((t) => t.stop());
      fallbackVideo.remove();
    }
  } catch (_) {}
  fallbackVideo = null;
}

function setMediaSession() {
  try {
    if (!("mediaSession" in navigator)) return;
    // eslint-disable-next-line no-undef
    navigator.mediaSession.metadata = new MediaMetadata({
      title: "Udyog Classes",
      artist: "Live class in progress",
    });
    navigator.mediaSession.playbackState = "playing";
  } catch (_) {}
}

// Chrome's Automatic Picture-in-Picture for video-conferencing sites
// (Chrome 120+, granted because the meeting holds the mic via getUserMedia).
// When the browser decides the page is occluded — minimised, app switched,
// tab changed — it calls this handler WITH activation, so PiP opens with no
// tap at all. This is the closest a website can get to Zoom's native
// "Back button shrinks into a floating window" behaviour.
function registerAutoPipHandler() {
  try {
    if (!("mediaSession" in navigator) || !navigator.mediaSession.setActionHandler) return;
    navigator.mediaSession.setActionHandler("enterpictureinpicture", async () => {
      if (!state.meetingActive || state.userExplicitlyLeft) return;
      log("auto-pip requested by browser");
      await enterPip("auto-mediasession");
    });
    log("auto-pip handler registered");
  } catch (e) {
    log("auto-pip handler unsupported", e?.message);
  }
}

function unregisterAutoPipHandler() {
  try {
    navigator.mediaSession?.setActionHandler?.("enterpictureinpicture", null);
  } catch (_) {}
}

// ---------------------------------------------------------------- doc PiP

let docPipWindow = null;

async function enterDocumentPip(video) {
  if (!supportsDocumentPip() || !video?.srcObject) return false;
  try {
    if (docPipWindow && !docPipWindow.closed) return true;
    const w = await window.documentPictureInPicture.requestWindow({
      width: 360,
      height: 220,
    });
    docPipWindow = w;
    w.document.body.style.margin = "0";
    w.document.body.style.background = "#000";
    const clone = w.document.createElement("video");
    clone.dataset.udyogPipClone = "1";
    clone.autoplay = true;
    clone.muted = true;
    clone.playsInline = true;
    clone.style.width = "100%";
    clone.style.height = "100%";
    clone.style.objectFit = "contain";
    clone.srcObject = video.srcObject;
    w.document.body.appendChild(clone);
    await clone.play().catch(() => {});
    w.addEventListener("pagehide", () => {
      docPipWindow = null;
      state.pipActive = false;
      log("document-pip closed");
    });
    state.pipActive = true;
    log("document-pip opened");
    return true;
  } catch (e) {
    state.lastError = e?.name || String(e);
    log("document-pip failed", state.lastError, e?.message);
    return false;
  }
}

function closeDocumentPip() {
  try {
    if (docPipWindow && !docPipWindow.closed) docPipWindow.close();
  } catch (_) {}
  docPipWindow = null;
}

// ---------------------------------------------------------------- enter/exit

export async function enterPip(reason = "manual") {
  if (state.userExplicitlyLeft) {
    log("enterPip skipped — user explicitly left");
    return { ok: false, error: "user-left" };
  }
  if (document.pictureInPictureElement || (docPipWindow && !docPipWindow.closed)) {
    state.pipActive = true;
    return { ok: true, error: null };
  }

  const video = pickVideo() || ensureFallbackVideo();
  log("enterPip", { reason, hasVideo: !!video, support: pipSupport() });
  if (!video) {
    state.lastError = "no-video";
    return { ok: false, error: "no-video" };
  }
  setMediaSession();

  if (video.readyState < 2) {
    await new Promise((res) => {
      const done = () => res();
      video.addEventListener("loadeddata", done, { once: true });
      setTimeout(done, 800);
    });
  }

  if (video.paused) {
    try {
      await video.play();
    } catch (_) {
      /* autoplay policy — PiP may still work */
    }
  }

  if (supportsVideoPip()) {
    try {
      await video.requestPictureInPicture();
      state.pipActive = true;
      state.pipPending = false;
      state.lastError = null;
      log("video PiP opened", reason);
      return { ok: true, error: null };
    } catch (e) {
      state.lastError = e?.name || String(e);
      log("video PiP rejected", state.lastError, e?.message);
    }
  }

  if (supportsSafariPip(video)) {
    try {
      video.webkitSetPresentationMode("picture-in-picture");
      state.pipActive = true;
      state.pipPending = false;
      log("safari PiP opened", reason);
      return { ok: true, error: null };
    } catch (e) {
      state.lastError = e?.name || String(e);
    }
  }

  if (await enterDocumentPip(video)) {
    state.pipPending = false;
    return { ok: true, error: null };
  }

  return { ok: false, error: state.lastError || "unsupported" };
}

export async function exitPip() {
  state.pipPending = false;
  closeDocumentPip();
  try {
    if (document.pictureInPictureElement) await document.exitPictureInPicture();
  } catch (e) {
    log("exitPip error", e?.name);
  }
  try {
    document.querySelectorAll("video").forEach((v) => {
      if (v.webkitPresentationMode === "picture-in-picture") v.webkitSetPresentationMode("inline");
    });
  } catch (_) {}
  state.pipActive = false;
}

// ---------------------------------------------------------------- lifecycle

let bound = false;
let unbind = () => {};

// Called from a real user gesture (dock button). Opens the floating window
// straight away — the browser only allows this with user activation, and once
// it is open it survives minimising, browser Back and tab switches. This is
// the only way to guarantee "PiP every time" on Chrome/Android.
export async function armFloating() {
  state.keepFloating = true;
  try {
    window.localStorage?.setItem("udyogKeepFloating", "1");
  } catch (_) {}
  return enterPip("armed-gesture");
}

export function disarmFloating() {
  state.keepFloating = false;
  try {
    window.localStorage?.removeItem("udyogKeepFloating");
  } catch (_) {}
  exitPip();
}

export const isFloatingArmed = () => state.keepFloating;

export function startMeetingSession() {
  state.meetingActive = true;
  state.userExplicitlyLeft = false;
  state.navigationAway = false;
  state.isRestoringMeeting = false;
  state.pipPending = false;
  log("meeting session started");
  bindLifecycle();
  setMediaSession();
  registerAutoPipHandler();
}

// The ONLY path that ends the meeting.
export function markUserLeft() {
  state.userExplicitlyLeft = true;
  state.meetingActive = false;
  state.pipPending = false;
  log("user explicitly left — PiP disabled");
  state.keepFloating = false;
  try {
    window.localStorage?.removeItem("udyogKeepFloating");
  } catch (_) {}
  exitPip();
  destroyFallbackVideo();
}

export function endMeetingSession() {
  state.meetingActive = false;
  state.pipPending = false;
  closeDocumentPip();
  destroyFallbackVideo();
  unregisterAutoPipHandler();
  unbindLifecycle();
  log("meeting session ended");
}

// A real gesture arms PiP. If PiP was requested while the page was hidden
// (where the browser forbids it) we retry here, the first legal moment.
function onGesture() {
  state.lastGestureAt = Date.now();
  // "Keep floating" armed but the window got closed (or was refused earlier):
  // re-open it now, while we legally hold user activation.
  if (
    state.keepFloating &&
    state.meetingActive &&
    !state.userExplicitlyLeft &&
    !state.pipActive &&
    !document.pictureInPictureElement
  ) {
    enterPip("keep-floating-gesture");
    return;
  }
  if (state.pipPending && state.meetingActive && !state.userExplicitlyLeft && !state.pageHidden) {
    log("retrying pending PiP on user gesture");
    state.pipPending = false;
    enterPip("pending-gesture");
  }
}

const hasFreshActivation = () => Date.now() - state.lastGestureAt < ACTIVATION_MS;

// Called by the page when the user navigates away (back button / route change).
export async function handleNavigationAway(reason = "popstate") {
  if (!state.meetingActive || state.userExplicitlyLeft) return { ok: false, error: "inactive" };
  state.navigationAway = true;
  log("navigation away", { reason, freshActivation: hasFreshActivation() });
  const res = await enterPip(reason);
  if (!res.ok && res.error === "NotAllowedError") state.pipPending = true;
  return res;
}

function onVisibilityChange() {
  const hidden = document.visibilityState === "hidden";
  state.pageHidden = hidden;
  log("visibilitychange", { hidden, meetingActive: state.meetingActive });
  if (!state.meetingActive || state.userExplicitlyLeft) return;
  if (hidden) {
    // Legal only with fresh activation (or installed-PWA auto-PiP, which the
    // browser does for us). Otherwise remember it and enter on return.
    if (hasFreshActivation()) enterPip("visibility-hidden");
    else {
      state.pipPending = true;
      log("PiP deferred — no user activation available while hiding");
    }
  } else {
    state.isRestoringMeeting = true;
    // Returning to the tab: hand the video back to the page unless the user
    // asked us to keep the floating window around.
    if (state.pipActive && !state.keepFloating) exitPip();
    state.pipPending = false;
    state.navigationAway = false;
    setTimeout(() => {
      state.isRestoringMeeting = false;
    }, 300);
  }
}

function onPageHide() {
  state.pageHidden = true;
  log("pagehide", { persisted: state.meetingActive });
  // Async PiP cannot be started reliably here — documented limitation.
}

function onPageShow() {
  state.pageHidden = false;
  state.isRestoringMeeting = true;
  log("pageshow");
  if (state.pipActive && !state.keepFloating) exitPip();
  setTimeout(() => {
    state.isRestoringMeeting = false;
  }, 300);
}

function onEnterPipEvent() {
  state.pipActive = true;
  log("enterpictureinpicture event");
}

function onLeavePipEvent() {
  state.pipActive = false;
  log("leavepictureinpicture event");
}

function bindLifecycle() {
  if (bound || typeof window === "undefined") return;
  bound = true;
  const gestureOpts = { capture: true, passive: true };
  window.addEventListener("pointerdown", onGesture, gestureOpts);
  window.addEventListener("keydown", onGesture, gestureOpts);
  window.addEventListener("touchstart", onGesture, gestureOpts);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  document.addEventListener("enterpictureinpicture", onEnterPipEvent, true);
  document.addEventListener("leavepictureinpicture", onLeavePipEvent, true);

  unbind = () => {
    window.removeEventListener("pointerdown", onGesture, gestureOpts);
    window.removeEventListener("keydown", onGesture, gestureOpts);
    window.removeEventListener("touchstart", onGesture, gestureOpts);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    document.removeEventListener("enterpictureinpicture", onEnterPipEvent, true);
    document.removeEventListener("leavepictureinpicture", onLeavePipEvent, true);
    bound = false;
  };
  log("lifecycle bound", pipSupport());
}

function unbindLifecycle() {
  unbind();
  unbind = () => {};
}

// Exposed for manual debugging from the console.
if (typeof window !== "undefined") {
  window.__udyogPip = { getMeetingState, enterPip, exitPip, pipSupport, armFloating, disarmFloating };
}
